import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import { IPagination } from "../../data/AppInterface";
import { deleteIcon, editIcon, eyeIcon } from "../../assets/icons/SvgIconList";
import {
  AdvanceSearch,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import { ORDER_BY } from "../../utils/AppConstants";
import MeasurementFieldForm from "./MeasurementFieldForm";
import { prepareMessageFromParams } from "../../utils/AppFunctions";
import {
  deleteMeasurementField,
  fetchAllMeasurementField,
  getMeasurementFields,
  IMeasurementField,
} from "./MeasurementFieldListController";

let timer: ReturnType<typeof setTimeout>;

const MeasurementFieldListView = () => {
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [measurementFieldList, setMeasurementFieldList] = useState<
    IMeasurementField[]
  >([]);
  const [measurementFieldToEdit, setMeasurementFieldToEdit] =
    useState<IMeasurementField>();
  const { pagination, setChangePagination } = UsePaginationHook();
  useEffect(() => {
    fetchMeasurementField({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch]);

  const fetchMeasurementField = (paginationPlayLoad: IPagination) => {
    setIsLoading(true);
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      await fetchAllMeasurementField(
        setMeasurementFieldList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
      );
      setIsLoading(false);
    }, 500);
  };

  const customActionView = (item: IMeasurementField) => {
    return (
      <div className="flex item-center justify-center w-full">
        <div
          className="size-5 cursor-pointer"
          title={t("view_tooltip_icon")}
          onClick={() => {
            setMeasurementFieldToEdit(item);
            setReadOnly(true);
            setShowForm(true);
          }}
        >
          {eyeIcon}
        </div>

        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("edit_tooltip_icon")}
          onClick={() => {
            setShowForm(true);
            setMeasurementFieldToEdit(item);
          }}
        >
          {editIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("delete_tooltip_icon")}
          onClick={() => {
            setShowDeleteConfirmation(true);
            setMeasurementFieldToEdit(item);
          }}
        >
          {deleteIcon}
        </div>
      </div>
    );
  };

  const handleDelete = async (value: boolean) => {
    if (value && measurementFieldToEdit) {
      const data = await deleteMeasurementField(measurementFieldToEdit.id);
      if (data) {
        fetchMeasurementField(pagination);
      }
    }
    setShowDeleteConfirmation(false);
    setMeasurementFieldToEdit(undefined);
  };

  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    setReadOnly(false);
    if (measurementFieldToEdit) {
      setMeasurementFieldToEdit(undefined);
    }
    if (status) {
      fetchMeasurementField(pagination);
    }
  };

  return (
    <div className="w-full overflow-auto scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setShowForm(true)}
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchMeasurementField(pagination);
        }}
      />

      <ICTable<IMeasurementField>
        isLoading={isLoading}
        totalFlex="grid grid-cols-14"
        fields={getMeasurementFields(customActionView, t)}
        data={measurementFieldList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchMeasurementField({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchMeasurementField({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchMeasurementField({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />

      {showForm && (
        <MeasurementFieldForm
          handleClose={handleCloseForm}
          measurementFieldToEdit={measurementFieldToEdit}
          readOnly={readOnly}
        />
      )}
      {showDeleteConfirmation && measurementFieldToEdit && (
        <ICConfirmationModal
          title={t("delete_measurement_field")}
          message={prepareMessageFromParams(t("delete_message"), [
            ["fieldName", measurementFieldToEdit.measurement_field_name],
          ])}
          onClose={(value) => handleDelete(value)}
        />
      )}
    </div>
  );
};

export default MeasurementFieldListView;
